<template>
    <b-modal
        id="closing-information-modal"
        :body-class="{'pt-0 pb-5': true, 'px-7': !isMobile}"
        title-tag="h2"
        title-class="position-absolute"
        title="Closing Information"
        hide-footer
    >
        <b-form @submit.prevent="saveClosingInformation" v-if="Boolean(orderAppResource._uuid)">
            <b-row
                class="mb-3"
                align-v="center"
            >
                <b-col
                    cols="4"
                    md="6"
                >
                    <label
                        class="text-gray-dark font-size-4 font-weight-bold mb-0"
                        for="closing-date"
                    >
                        Closing Date
                    </label>
                </b-col>

                <b-col
                    cols="8"
                    md="6"
                >
                    <b-input-group>
                        <b-input-group-prepend>
                            <span class="input-group-text-coherent">
                                <i class="far fa-calendar-day font-size-3"/>
                            </span>
                        </b-input-group-prepend>

                        <flat-pickr
                            id="closing-date"
                            class="form-control form-control-coherent"
                            :config="closingDateConfig"
                            v-model="formData.closingDate"
                        />
                    </b-input-group>
                    <p v-if="closingDateError" class="closing-date-error">{{ closingDateErrorMessage }}</p>

                    <form-errors
                        field="closingDate"
                        :errors="formErrors"
                    />
                </b-col>
            </b-row>

            <b-row
                class="mb-3"
                align-v="center"
            >
                <b-col
                    cols="4"
                    md="6"
                >
                    <label
                        class="text-gray-dark font-size-4 font-weight-bold mb-0"
                        for="title-company"
                    >
                        Title Company Name
                    </label>
                </b-col>

                <b-col
                    cols="8"
                    md="6"
                >
                    <b-form-input
                        id="title-company"
                        v-model="formData.titleCompanyName"
                    />

                    <form-errors
                        field="titleCompanyName"
                        :errors="formErrors"
                    />
                </b-col>
            </b-row>

            <b-row
                class="mb-3"
                align-v="center"
            >
                <b-col
                    cols="4"
                    md="6"
                >
                    <label
                        class="text-gray-dark font-size-4 font-weight-bold mb-0"
                        for="escrow-officer-name"
                    >
                        Escrow Officer Name
                    </label>
                </b-col>

                <b-col
                    cols="8"
                    md="6"
                >
                    <b-form-input
                        id="escrow-officer-name"
                        v-model="formData.escrowOfficerFullName"
                    />

                    <form-errors
                        field="escrowOfficerFullName"
                        :errors="formErrors"
                    />
                </b-col>
            </b-row>

            <b-row
                class="mb-5"
                align-v="center"
            >
                <b-col
                    cols="4"
                    md="6"
                >
                    <label
                        class="text-gray-dark font-size-4 font-weight-bold mb-0"
                        for="escrow-officer-email"
                        type="email"
                    >
                        Escrow Officer Email
                    </label>
                </b-col>

                <b-col
                    cols="8"
                    md="6"
                >
                    <b-form-input
                        id="escrow-officer-email"
                        v-model="formData.escrowOfficerEmail"
                    />

                    <form-errors
                        field="escrowOfficerEmail"
                        :errors="formErrors"
                    />
                </b-col>
            </b-row>

            <b-row align-h="center">
                <b-col cols="6">
                    <b-button
                        block
                        variant="primary"
                        size="lg"
                        type="submit"
                    >
                        <b-spinner
                            class="mr-1"
                            small
                            v-if="saving"
                        />

                        Save
                    </b-button>
                </b-col>
            </b-row>
        </b-form>
    </b-modal>
</template>

<script>
import {cloneDeep} from 'lodash';
import FlatPickr from 'vue-flatpickr-component';
import FormErrorsMixin from '@sellers-shield-home-protection/ss-ui-kit/src/components/FormErrors/FormErrorsMixin';
import EscrowOfficerAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/EscrowOfficerAppResource';
import IsMobileMixin from "@sellers-shield-home-protection/ss-ui-kit/src/mixins/IsMobileMixin";
import HslpMixin from "@mixins/HslpMixin";
import {getUTCDate} from '@helpers/dates';

export default {
    name: 'ClosingInformationModalV1',
    mixins: [
        FormErrorsMixin,
        IsMobileMixin,
        HslpMixin
    ],
    components: {
        FlatPickr,
    },
    props: {
        orderAppResource: {
            type: Object,
        },
    },
    data: () => ({
        formData: {
            closingDate: null,
            titleCompanyName: '',
            escrowOfficerFullName: '',
            escrowOfficerEmail: '',
        },
        closingDateConfig: {
            wrap: true,
            altFormat: 'M j, Y',
            dateFormat: 'Y-m-d',
        },
        saving: false,
    }),
    watch: {
        orderAppResource: {
            immediate: true,
            handler() {
                if (this.orderAppResource.closingDate) {
                    this.$set(this.formData, 'closingDate', this.closingDateUTC);
                }

                if (this.orderAppResource.titleCompanyName) {
                    this.$set(this.formData, 'titleCompanyName', this.orderAppResource.titleCompanyName);
                }

                if (this.orderAppResource.escrowOfficer) {
                    if (this.orderAppResource.escrowOfficer.fullName) {
                        this.$set(this.formData, 'escrowOfficerFullName', this.orderAppResource.escrowOfficer.fullName);
                    }

                    if (this.orderAppResource.escrowOfficer.email) {
                        this.$set(this.formData, 'escrowOfficerEmail', this.orderAppResource.escrowOfficer.email);
                    }
                }
            },
        },
    },
    computed:{
        closingDateUTC(){
            if(this.orderAppResource.closingDate){
                return getUTCDate(this.orderAppResource.closingDate);
            }
            return null;            
        }
    },
    methods: {
        async saveClosingInformation() {

            if (!this.validateClosingDate()) {
                return false;
            }

            let orderAppResource = cloneDeep(this.orderAppResource);

            if (orderAppResource.uuid) {
                let escrowOfficerAppResource = cloneDeep(this.orderAppResource.escrowOfficer) || new EscrowOfficerAppResource({});

                orderAppResource.setAttribute('closingDate', this.formData.closingDate);
                orderAppResource.setAttribute('titleCompanyName', this.formData.titleCompanyName);
                escrowOfficerAppResource.setAttribute('fullName', this.formData.escrowOfficerFullName);
                escrowOfficerAppResource.setAttribute('email', !this.formData.escrowOfficerEmail? null: this.formData.escrowOfficerEmail);
                escrowOfficerAppResource.setAttribute('orderUuid', orderAppResource.uuid);

                await this.catchAppResourceErrors(async () => {
                    await orderAppResource.save();
                    await escrowOfficerAppResource.save();
                    this.$emit('save');
                }, 'saving');
            }
        },
    },
}
</script>
